/**
 * Start playback on a media element without leaking the one rejection that is
 * not a fault.
 *
 * `play()` is asynchronous: with `preload="none"` it first has to fetch and
 * buffer the source, so on a slow connection the promise stays pending for
 * seconds. If anything pauses the element in that window — the user tapping
 * the native controls, the page switching to a different source, a WebView
 * backgrounding the tab — the browser rejects it with an `AbortError`
 * ("The play() request was interrupted by a call to pause()"). That is the
 * outcome the caller asked for, not an error, so it is swallowed here; left
 * unhandled it surfaces as an uncaught rejection in every error sink (Sentry
 * issue 7782100217). Everything else — no supported source, autoplay policy —
 * still rejects so the caller can recover and the sink can see it.
 *
 * Older WebViews return `undefined` from `play()`; that counts as started.
 */
export const playMedia = async (media: HTMLMediaElement): Promise<void> => {
    try {
        await media.play();
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            return;
        }

        throw error;
    }
};
